<template>
  <article class="char-card" :class="{ 'is-generating': isGenerating, 'is-failed': isFailed }">
    <div class="card-tools">
      <el-button
        v-if="!isGenerating"
        class="tool-button edit-tool"
        circle
        text
        aria-label="编辑角色"
        @click.stop="emit('edit')"
      >
        <el-icon class="tool-icon"><Edit /></el-icon>
      </el-button>
      <el-button
        class="tool-button delete-tool"
        circle
        text
        aria-label="删除角色"
        @click.stop="emit('delete')"
      >
        <el-icon class="tool-icon"><Delete /></el-icon>
      </el-button>
    </div>

    <div class="card-preview">
      <div class="avatar-stage" :class="{ 'is-loading': isGenerating, 'is-failed': isFailed }">
        <div v-if="isGenerating" class="card-loading-state">
          <CharacterLoadingIcon class="card-loading-icon" />
          <span class="card-loading-text">角色正在生成中...</span>
        </div>
        <div v-else-if="isFailed" class="card-failed-state">
          <SvgIcon class="card-failed-icon" name="character-generation-failed" />
          <span class="card-failed-text">生成失败</span>
        </div>
        <img
          v-else-if="character.avatarUrl"
          :src="character.avatarUrl"
          :alt="displayName"
          class="card-avatar-img"
        />
        <div v-else class="card-avatar-placeholder" :style="{ background: fallbackColor }">
          {{ displayName[0] ?? '?' }}
        </div>
      </div>

      <div class="card-copy">
        <h2 class="card-name">{{ displayName }}</h2>
        <div class="card-desc-row">
          <p class="card-desc">{{ summary }}</p>
          <div class="mobile-actions">
            <button
              class="mobile-more-trigger"
              type="button"
              aria-label="角色操作"
              @click.stop="openMobileMenu"
            >
              <SvgIcon name="character-more" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="card-footer">
      <el-button
        class="start-button"
        type="primary"
        :icon="EditPen"
        :disabled="isCreationDisabled"
        @click="emit('start')"
      >
        开始创作
      </el-button>
    </div>
  </article>

  <Teleport to="body">
    <Transition name="mobile-action-sheet">
      <div v-if="mobileMenuVisible" class="mobile-menu-overlay" @click.self="closeMobileMenu">
        <section class="mobile-menu-sheet" role="dialog" aria-modal="true" aria-label="编辑角色">
          <div class="mobile-menu-header">
            <h2 class="mobile-menu-title">编辑角色</h2>
            <button
              class="mobile-menu-collapse"
              type="button"
              aria-label="关闭菜单"
              @click="closeMobileMenu"
            >
              <SvgIcon name="character-menu-collapse" />
            </button>
          </div>

          <div class="mobile-menu-list">
            <button
              v-if="!isGenerating"
              class="mobile-menu-item"
              type="button"
              @click="handleMobileAction('edit')"
            >
              <SvgIcon class="mobile-menu-icon" name="character-menu-edit" />
              <span>编辑</span>
            </button>
            <button class="mobile-menu-item" type="button" @click="handleMobileAction('delete')">
              <SvgIcon class="mobile-menu-icon" name="character-menu-delete" />
              <span>删除</span>
            </button>
          </div>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { Delete, Edit, EditPen } from '@element-plus/icons-vue';
  import type { CharacterItem } from '@/api/character';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import CharacterLoadingIcon from './CharacterLoadingIcon.vue';

  const props = defineProps<{
    character: CharacterItem;
    summary: string;
    fallbackColor: string;
  }>();

  const emit = defineEmits<{
    edit: [];
    delete: [];
    start: [];
  }>();

  const mobileMenuVisible = ref(false);
  const isGenerating = computed(
    () =>
      props.character.status === 'queued' ||
      props.character.status === 'running' ||
      props.character.status === 'processing'
  );
  const isFailed = computed(() => props.character.status === 'failed');
  const isCreationDisabled = computed(() => isGenerating.value || isFailed.value);
  const displayName = computed(() => props.character.name || '未命名');

  function openMobileMenu() {
    mobileMenuVisible.value = true;
  }

  function closeMobileMenu() {
    mobileMenuVisible.value = false;
  }

  function handleMobileAction(action: 'edit' | 'delete') {
    closeMobileMenu();
    if (action === 'edit') emit('edit');
    if (action === 'delete') emit('delete');
  }
</script>

<style scoped>
  .char-card {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 236px;
    height: 280px;
    box-sizing: border-box;
    overflow: hidden;
    border: 0;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    transition:
      box-shadow 0.16s ease,
      transform 0.16s ease;
  }

  .char-card:hover {
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }

  .card-tools {
    position: absolute;
    top: 8px;
    right: 12px;
    z-index: 2;
    display: flex;
    gap: 2px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.16s ease;
  }

  .char-card:hover .card-tools,
  .char-card:focus-within .card-tools {
    opacity: 1;
    pointer-events: auto;
  }

  .tool-button.el-button {
    --el-button-hover-bg-color: rgba(251, 249, 255, 1);
    --el-button-hover-text-color: #6033e1;
    --el-button-active-bg-color: rgba(251, 249, 255, 1);
    --el-button-active-text-color: #6033e1;

    width: 30px;
    height: 30px;
    margin: 0;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #444e63;
  }

  .tool-button.el-button:hover,
  .tool-button.el-button:focus {
    background: rgba(251, 249, 255, 1);
    color: #6033e1;
  }

  .tool-icon {
    font-size: 16px;
    line-height: 1;
  }

  .tool-button :deep(svg) {
    display: block;
    color: currentColor;
  }

  .card-preview {
    height: 229px;
    padding-top: 20px;
    box-sizing: border-box;
  }

  .avatar-stage {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    width: 160px;
    height: 160px;
    margin: 0 auto;
  }

  .avatar-stage.is-loading {
    align-items: center;
    border-radius: 8px;
    background: #fbf9ff;
  }

  .avatar-stage.is-failed {
    align-items: center;
  }

  .card-loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }

  .card-loading-icon {
    display: block;
    width: 24px;
    height: 24px;
    animation: character-card-spin 0.9s linear infinite;
  }

  .card-loading-text {
    margin-top: 12px;
    color: #6e6a7c;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 13px;
    font-weight: 400;
    line-height: 14px;
  }

  .card-failed-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }

  .card-failed-icon {
    width: 40px;
    height: 40px;
  }

  .card-failed-text {
    color: #6e6a7c;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 13px;
    font-weight: 400;
    line-height: 14px;
  }

  .card-avatar-img {
    display: block;
    max-width: 160px;
    max-height: 160px;
    object-fit: contain;
  }

  .card-avatar-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 112px;
    height: 112px;
    border-radius: 28px;
    color: #fff;
    font-size: 36px;
    font-weight: 700;
  }

  .card-copy {
    margin-top: 7px;
    text-align: center;
  }

  .card-name {
    margin: 0;
    color: #160211;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 13px;
    font-weight: 500;
    line-height: 14px;
  }

  .card-desc-row {
    min-width: 0;
  }

  .card-desc {
    width: 196px;
    margin: 8px auto 0;
    overflow: hidden;
    color: #6e6a7c;
    font-family: Inter, 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
    font-size: 13px;
    font-weight: 400;
    line-height: 14px;
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .mobile-actions {
    display: none;
  }

  .mobile-more-trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #160211;
    cursor: pointer;
  }

  .mobile-more-trigger .svg-icon {
    width: 16px;
    height: 16px;
  }

  .mobile-menu-overlay {
    display: none;
  }

  .card-footer {
    height: 52px;
    box-sizing: border-box;
    padding: 8px 12px 12px;
  }

  .char-card.is-failed .card-footer {
    opacity: 0.6;
  }

  .start-button.el-button {
    width: 212px;
    height: 32px;
    border: 0;
    border-radius: 10px;
    background: #6033e1;
    color: #fff;
    font-family: 'Noto Sans SC', 'Source Han Sans CN VF', 'Microsoft YaHei', sans-serif;
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
  }

  .start-button.el-button:hover,
  .start-button.el-button:focus {
    background: #6033e1;
    color: #fff;
  }

  .start-button.el-button.is-disabled,
  .start-button.el-button.is-disabled:hover,
  .start-button.el-button.is-disabled:focus {
    border: 0;
    background: #6033e1;
    color: #fff;
    opacity: 0.8;
  }

  .char-card.is-failed .start-button.el-button.is-disabled,
  .char-card.is-failed .start-button.el-button.is-disabled:hover,
  .char-card.is-failed .start-button.el-button.is-disabled:focus {
    opacity: 1;
  }

  @keyframes character-card-spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (min-width: 768px) and (max-width: 900px) {
    .char-card {
      width: 100%;
    }

    .card-desc {
      width: calc(100% - 40px);
    }

    .start-button.el-button {
      width: 100%;
    }
  }

  @media (max-width: 767px) {
    .char-card {
      width: 100%;
      height: 226px;
      overflow: visible;
      border-radius: 0;
      background: transparent;
      box-shadow: none;
      gap: 8px;
      transition: none;
    }

    .char-card:hover {
      box-shadow: none;
      transform: none;
    }

    .card-tools,
    .card-footer {
      display: none;
    }

    .card-preview {
      display: flex;
      flex-direction: column;
      height: auto;
      padding: 0;
      gap: 8px;
    }

    .avatar-stage {
      align-items: center;
      width: 100%;
      height: 182px;
      margin: 0;
      padding: 20px 16px;
      box-sizing: border-box;
      overflow: hidden;
      border-radius: 12px;
      background: #f5f0ff;
    }

    .avatar-stage.is-loading {
      border-radius: 12px;
      background: #fbf9ff;
    }

    .card-avatar-img {
      max-width: 100%;
      max-height: 142px;
      width: auto;
      height: auto;
    }

    .card-avatar-placeholder {
      width: 112px;
      height: 112px;
      border-radius: 28px;
      font-size: 36px;
    }

    .card-copy {
      display: flex;
      flex-direction: column;
      height: 36px;
      margin-top: 0;
      text-align: left;
      gap: 8px;
    }

    .card-name {
      width: 100%;
      overflow: hidden;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      line-height: 14px;
      text-align: left;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .card-desc-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      height: 14px;
      gap: 8px;
    }

    .card-desc {
      flex: 1;
      width: auto;
      margin: 0;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      line-height: 14px;
      text-align: left;
    }

    .mobile-actions {
      display: inline-flex;
      flex: 0 0 auto;
      width: 20px;
      height: 20px;
      margin-top: -3px;
      margin-right: -5px;
    }

    .mobile-menu-overlay {
      position: fixed;
      inset: 0;
      z-index: 3000;
      display: block;
      background: rgba(0, 0, 0, 0.3);
    }

    .mobile-menu-sheet {
      position: absolute;
      right: 0;
      bottom: 0;
      left: 0;
      min-height: 164px;
      box-sizing: border-box;
      padding: 16px 16px calc(16px + env(safe-area-inset-bottom));
      border-radius: 16px 16px 0 0;
      background: #fff;
    }

    .mobile-menu-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      height: 24px;
    }

    .mobile-menu-title {
      margin: 0;
      color: #160211;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      font-weight: 600;
      line-height: 16px;
    }

    .mobile-menu-collapse {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 44px;
      height: 24px;
      padding: 0;
      border: 1px solid #eff0f7;
      border-radius: 66px;
      background: #fff;
      color: #160211;
      cursor: pointer;
    }

    .mobile-menu-collapse .svg-icon {
      width: 12px;
      height: 12px;
      fill: none;
      stroke: currentColor;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-width: 1.4;
    }

    .mobile-menu-list {
      display: flex;
      flex-direction: column;
      margin-top: 16px;
      gap: 8px;
    }

    .mobile-menu-item {
      display: inline-flex;
      align-items: center;
      width: 100%;
      height: 42px;
      box-sizing: border-box;
      padding: 10px;
      border: 0;
      background: transparent;
      color: #160211;
      cursor: pointer;
      font: inherit;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      font-weight: 500;
      line-height: 16px;
      text-align: left;
      gap: 8px;
    }

    .mobile-menu-item:active {
      background: #fbf9ff;
    }

    .mobile-menu-icon {
      width: 16px;
      height: 16px;
      fill: none;
      stroke: currentColor;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-width: 1.4;
    }

    .mobile-action-sheet-enter-active,
    .mobile-action-sheet-leave-active {
      transition: opacity 0.18s ease;
    }

    .mobile-action-sheet-enter-from,
    .mobile-action-sheet-leave-to {
      opacity: 0;
    }

    .mobile-action-sheet-enter-active .mobile-menu-sheet,
    .mobile-action-sheet-leave-active .mobile-menu-sheet {
      transition: transform 0.18s ease;
    }

    .mobile-action-sheet-enter-from .mobile-menu-sheet,
    .mobile-action-sheet-leave-to .mobile-menu-sheet {
      transform: translateY(100%);
    }
  }
</style>
